import { ChangeDetectorRef, Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbModal, NgbPagination } from '@ng-bootstrap/ng-bootstrap';
import { ThaiDateTimePipe } from '../../../shared/commons/thai-datetime.pipe';

import { firstValueFrom } from 'rxjs';
import { ProductGroupDto } from '../../../../generated-model/models';
import { ProductCodeDto, ProductCodeService } from './product-code-service';
import { ProductCodeModal } from './product-code-modal';
import { ConfirmDialogService } from '../../../shared/ui/confirm-dialog/confirm-dialog.service';
import { PageTitleComponent } from '../../../shared/commons/page-title/page-title.component';
import { resolveTotalElements } from '../../../shared/commons/pagination.util';
import { PagesizeSelectComponent } from '../../../shared/components/pagesize-select/pagesize-select.component';
import { StatusBadgeComponent } from '../../../shared/components/status-badge/status-badge.component';
import {SearchIconComponent} from '../../../shared/components/search-icon/search-icon.component';

@Component({
  selector: 'app-product-code',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbPagination, ThaiDateTimePipe, PageTitleComponent, PagesizeSelectComponent, StatusBadgeComponent, SearchIconComponent],
  templateUrl: './product-code.html',
  styleUrl: './product-code.scss',
})
export class ProductCodeComponent implements OnInit {
  private readonly service = inject(ProductCodeService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalService = inject(NgbModal);
  private readonly confirmDialogService = inject(ConfirmDialogService);

  protected readonly searchQuery = signal('');
  protected readonly stateFilter = signal<number | null>(null);
  protected readonly isLoading = signal(false);
  protected readonly page = signal(1);
  protected readonly pageSize = signal(10);
  protected readonly collectionSize = signal(0);
  protected readonly displayedItems = signal<ProductCodeDto[]>([]);

  productGroups: ProductGroupDto[] = [];
  productGroupById: Record<number, ProductGroupDto> = {};

  private totalElements = 0;

  ngOnInit(): void {
    this.loadProductGroups();
    this.load();
  }

  loadProductGroups(): void {
    this.service.getActiveProductGroups().subscribe({
      next: (data) => {
        this.productGroups = data;
        const map: Record<number, ProductGroupDto> = {};
        for (const item of data) {
          if (item.id != null) {
            map[item.id] = item;
          }
        }
        this.productGroupById = map;
        this.cdr.detectChanges();
      },
      error: () => {
        this.productGroups = [];
        this.productGroupById = {};
        this.cdr.detectChanges();
      }
    });
  }

  load(): void {
    this.isLoading.set(true);
    this.service.getAllProductCodes(this.page() - 1, this.pageSize(), this.searchQuery(), this.stateFilter()).subscribe({
      next: (data) => {
        this.displayedItems.set(data.content);
        this.totalElements = resolveTotalElements(data);
        this.collectionSize.set(this.totalElements);
        this.isLoading.set(false);
        this.cdr.detectChanges();
      },
      error: () => {
        this.isLoading.set(false);
        this.cdr.detectChanges();
      }
    });
  }

  onSearchChange(): void {
    this.page.set(1);
    this.load();
  }

  clearSearch(): void {
    this.searchQuery.set('');
    this.stateFilter.set(null);
    this.onSearchChange();
  }

  openAddModal(): void {
    const modalRef = this.modalService.open(ProductCodeModal, { size: 'xl' });
    modalRef.componentInstance.productGroups = this.productGroups;
    modalRef.componentInstance.onSave = async (result: ProductCodeDto): Promise<boolean> => {
      const confirmed = await this.confirmDialogService.confirmSave();
      if (!confirmed) {
        return false;
      }
      try {
        await firstValueFrom(this.service.createProductCode(result));
        this.load();
        return true;
      } catch {
        return false;
      }
    };
  }

  openEditModal(item: ProductCodeDto): void {
    const modalRef = this.modalService.open(ProductCodeModal, { size: 'xl' });
    modalRef.componentInstance.item = item;
    modalRef.componentInstance.productGroups = this.productGroups;
    modalRef.componentInstance.onSave = async (result: ProductCodeDto): Promise<boolean> => {
      const confirmed = await this.confirmDialogService.confirmSave();
      if (!confirmed || result.id == null) {
        return false;
      }
      try {
        await firstValueFrom(this.service.updateProductCode(result.id, result));
        this.load();
        return true;
      } catch {
        return false;
      }
    };
  }

  openViewModal(item: ProductCodeDto): void {
    const modalRef = this.modalService.open(ProductCodeModal, { size: 'xl' });
    modalRef.componentInstance.item = item;
    modalRef.componentInstance.productGroups = this.productGroups;
    modalRef.componentInstance.viewOnly = true;
  }

  async delete(id: number): Promise<void> {
    const confirmed = await this.confirmDialogService.confirmDelete('คุณต้องการลบข้อมูลนี้ใช่หรือไม่?');
    if (!confirmed) {
      return;
    }
    this.service.deleteProductCode(id).subscribe(() => this.load());
  }

  getProductGroupLabel(productGroupId?: number): string {
    if (!productGroupId) {
      return '-';
    }
    const item = this.productGroupById[productGroupId];
    return item?.title ?? String(productGroupId);
  }
}
